import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useAudit } from '../hooks/useAudit'
import { useVisits } from '../hooks/useVisits'
import type { AuditAction, AuditResource, AuditEntry } from '../types'
import { Card, CardContent } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '@/components/ui/table'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Skeleton } from '@/components/ui/skeleton'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { ChevronLeft, ChevronRight, Clock } from 'lucide-react'
import { GuideApprovalsAudit } from '@/components/GuideApprovalsAudit'

const PAGE_SIZE = 30

const actionColors: Record<AuditAction, string> = {
  create: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-300 border-0',
  update: 'bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-300 border-0',
  delete: 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300 border-0',
  sync: 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300 border-0',
}

const resourceLabels: Record<AuditResource, string> = {
  seed: 'Seed',
  client: 'Client',
  share: 'Share',
  allowed_domain: 'Allowed Domain',
  guide: 'Guide publication',
}

function resourceLink(resource: AuditResource, resourceId: string): string | null {
  switch (resource) {
    case 'seed': return `/seeds/${resourceId}`
    case 'share': return `/shares/${resourceId}`
    case 'client': return `/clients`
    case 'guide': return '/guides'
    default: return null
  }
}

function relativeTime(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime()
  const seconds = Math.floor(diff / 1000)
  if (seconds < 60) return 'just now'
  const minutes = Math.floor(seconds / 60)
  if (minutes < 60) return `${minutes}m ago`
  const hours = Math.floor(minutes / 60)
  if (hours < 24) return `${hours}h ago`
  const days = Math.floor(hours / 24)
  if (days < 30) return `${days}d ago`
  return new Date(iso).toLocaleDateString()
}

function formatValue(value: unknown): string {
  if (value === null || value === undefined) return 'null'
  if (typeof value === 'boolean') return value ? 'true' : 'false'
  if (Array.isArray(value)) return value.join(', ')
  if (typeof value === 'object') return JSON.stringify(value)
  return String(value)
}

function DetailView({ entry }: { entry: AuditEntry }) {
  if (!entry.detail) return null
  const { fields } = entry.detail as { fields?: Record<string, { from: unknown; to: unknown }> }
  if (!fields || Object.keys(fields).length === 0) return null

  return (
    <div className="mt-2 text-xs bg-muted/50 rounded p-2 font-mono max-w-xl space-y-1">
      {Object.entries(fields).map(([key, change]) => (
        <div key={key} className="flex gap-2 items-baseline">
          <span className="text-muted-foreground shrink-0">{key}:</span>
          <span className="text-red-500/80 line-through truncate">{formatValue(change.from)}</span>
          <span className="text-muted-foreground shrink-0">&rarr;</span>
          <span className="text-emerald-600 dark:text-emerald-400 truncate">{formatValue(change.to)}</span>
        </div>
      ))}
    </div>
  )
}

const eventLabels: Record<string, string> = {
  landing_enter: 'Landing',
  console_enter: 'Console',
  seed_view: 'Container',
  app_view: 'App',
}

const eventColors: Record<string, string> = {
  landing_enter: 'bg-blue-50 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300 border-0',
  console_enter: 'bg-purple-50 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300 border-0',
  seed_view: 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300 border-0',
  app_view: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-300 border-0',
}

export function AuditPage() {
  const [resourceFilter, setResourceFilter] = useState('all')
  const [offset, setOffset] = useState(0)
  const [expandedId, setExpandedId] = useState<number | null>(null)

  // Visits tab state
  const [eventFilter, setEventFilter] = useState('all')
  const [visitOffset, setVisitOffset] = useState(0)

  const { visits, total: visitTotal, loading: visitLoading } = useVisits({
    event: eventFilter === 'all' ? undefined : eventFilter,
    limit: PAGE_SIZE,
    offset: visitOffset,
  })

  const { entries, total, loading } = useAudit({
    resource: resourceFilter === 'all' ? undefined : resourceFilter as AuditResource,
    limit: PAGE_SIZE,
    offset,
  })

  const totalPages = Math.ceil(total / PAGE_SIZE)
  const currentPage = Math.floor(offset / PAGE_SIZE) + 1

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-foreground">Audit</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Track all changes across the platform
        </p>
      </div>

      <Tabs defaultValue="visits">
        <TabsList>
          <TabsTrigger value="visits">Visits</TabsTrigger>
          <TabsTrigger value="changes">Changes</TabsTrigger>
          <TabsTrigger value="guide-approvals">Guide approvals</TabsTrigger>
        </TabsList>

        <TabsContent value="visits" className="mt-4">
          {/* Filters */}
          <div className="flex items-center gap-3 mb-4">
            <Select value={eventFilter} onValueChange={(v) => { setEventFilter(v); setVisitOffset(0) }}>
              <SelectTrigger className="w-[160px]">
                <SelectValue placeholder="All events" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All events</SelectItem>
                <SelectItem value="landing_enter">Landing</SelectItem>
                <SelectItem value="console_enter">Console</SelectItem>
                <SelectItem value="seed_view">Container</SelectItem>
                <SelectItem value="app_view">App</SelectItem>
              </SelectContent>
            </Select>
            <span className="text-sm text-muted-foreground ml-auto">
              {visitTotal} {visitTotal === 1 ? 'visit' : 'visits'}
            </span>
          </div>

          <Card>
            <CardContent className="p-0">
              {visitLoading ? (
                <div className="p-4 space-y-3">
                  {Array.from({ length: 5 }).map((_, i) => (
                    <Skeleton key={i} className="h-10 w-full" />
                  ))}
                </div>
              ) : visits.length === 0 ? (
                <div className="py-12 text-center text-sm text-muted-foreground">
                  No visits recorded yet
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-[100px]">Time</TableHead>
                      <TableHead className="w-[160px]">User</TableHead>
                      <TableHead className="w-[80px]">Event</TableHead>
                      <TableHead>Seed</TableHead>
                      <TableHead>Share</TableHead>
                      <TableHead>App</TableHead>
                      <TableHead className="w-[70px]">Country</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {visits.map(visit => (
                      <TableRow key={visit.id}>
                        <TableCell className="text-muted-foreground whitespace-nowrap">
                          <div className="flex items-center gap-1.5">
                            <Clock className="size-3 text-muted-foreground/60" />
                            <span title={new Date(visit.timestamp).toLocaleString()}>{relativeTime(visit.timestamp)}</span>
                          </div>
                        </TableCell>
                        <TableCell className="text-muted-foreground font-mono text-xs truncate max-w-[160px]">
                          {visit.email}
                        </TableCell>
                        <TableCell>
                          <Badge variant="secondary" className={`whitespace-nowrap ${eventColors[visit.event] || ''}`}>
                            {eventLabels[visit.event] || visit.event}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-sm">
                          {visit.seed ? (
                            <Link to={`/seeds/${visit.seed}`} className="text-primary hover:underline">
                              {visit.seed}
                            </Link>
                          ) : (
                            <span className="text-muted-foreground">-</span>
                          )}
                        </TableCell>
                        <TableCell className="text-sm text-muted-foreground">
                          {visit.shareName || '-'}
                        </TableCell>
                        <TableCell className="text-sm text-muted-foreground">
                          {visit.app || '-'}
                        </TableCell>
                        <TableCell className="text-sm text-muted-foreground">
                          {visit.country || '-'}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>

          {/* Pagination */}
          {(() => {
            const visitTotalPages = Math.ceil(visitTotal / PAGE_SIZE)
            const visitCurrentPage = Math.floor(visitOffset / PAGE_SIZE) + 1
            return visitTotalPages > 1 ? (
              <div className="flex items-center justify-between mt-4">
                <span className="text-sm text-muted-foreground">
                  Page {visitCurrentPage} of {visitTotalPages}
                </span>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={visitOffset === 0}
                    onClick={() => setVisitOffset(Math.max(0, visitOffset - PAGE_SIZE))}
                  >
                    <ChevronLeft className="size-4" />
                    Previous
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={visitOffset + PAGE_SIZE >= visitTotal}
                    onClick={() => setVisitOffset(visitOffset + PAGE_SIZE)}
                  >
                    Next
                    <ChevronRight className="size-4" />
                  </Button>
                </div>
              </div>
            ) : null
          })()}
        </TabsContent>

        <TabsContent value="changes" className="mt-4">
          {/* Filters */}
          <div className="flex items-center gap-3 mb-4">
            <Select value={resourceFilter} onValueChange={(v) => { setResourceFilter(v); setOffset(0) }}>
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="All resources" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All resources</SelectItem>
                <SelectItem value="seed">Seeds</SelectItem>
                <SelectItem value="client">Clients</SelectItem>
                <SelectItem value="share">Shares</SelectItem>
                <SelectItem value="allowed_domain">Allowed Domains</SelectItem>
                <SelectItem value="guide">Guide publications</SelectItem>
              </SelectContent>
            </Select>
            <span className="text-sm text-muted-foreground ml-auto">
              {total} {total === 1 ? 'entry' : 'entries'}
            </span>
          </div>

          {/* Table */}
          <Card>
            <CardContent className="p-0">
              {loading ? (
                <div className="p-4 space-y-3">
                  {Array.from({ length: 5 }).map((_, i) => (
                    <Skeleton key={i} className="h-10 w-full" />
                  ))}
                </div>
              ) : entries.length === 0 ? (
                <div className="py-12 text-center text-sm text-muted-foreground">
                  No audit entries yet
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-[120px]">Time</TableHead>
                      <TableHead className="w-[160px]">User</TableHead>
                      <TableHead className="w-[90px]">Action</TableHead>
                      <TableHead className="w-[120px]">Resource</TableHead>
                      <TableHead>Summary</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {entries.map(entry => {
                      const link = resourceLink(entry.resource, entry.resourceId)
                      const expanded = expandedId === entry.id
                      return (
                        <TableRow
                          key={entry.id}
                          className="cursor-pointer"
                          onClick={() => setExpandedId(expanded ? null : entry.id)}
                        >
                          <TableCell className="text-muted-foreground whitespace-nowrap">
                            <div className="flex items-center gap-1.5">
                              <Clock className="size-3 text-muted-foreground/60" />
                              <span title={new Date(entry.createdAt).toLocaleString()}>{relativeTime(entry.createdAt)}</span>
                            </div>
                          </TableCell>
                          <TableCell className="text-muted-foreground font-mono text-xs truncate max-w-[160px]">
                            {entry.userEmail}
                          </TableCell>
                          <TableCell>
                            <Badge variant="secondary" className={`whitespace-nowrap ${actionColors[entry.action] || ''}`}>
                              {entry.action}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            {link ? (
                              <Link
                                to={link}
                                className="text-primary hover:underline text-sm"
                                onClick={e => e.stopPropagation()}
                              >
                                {resourceLabels[entry.resource] || entry.resource}
                              </Link>
                            ) : (
                              <span className="text-sm">{resourceLabels[entry.resource] || entry.resource}</span>
                            )}
                          </TableCell>
                          <TableCell>
                            <div className="text-sm">{entry.summary}</div>
                            {expanded && <DetailView entry={entry} />}
                          </TableCell>
                        </TableRow>
                      )
                    })}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between mt-4">
              <span className="text-sm text-muted-foreground">
                Page {currentPage} of {totalPages}
              </span>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={offset === 0}
                  onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}
                >
                  <ChevronLeft className="size-4" />
                  Previous
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={offset + PAGE_SIZE >= total}
                  onClick={() => setOffset(offset + PAGE_SIZE)}
                >
                  Next
                  <ChevronRight className="size-4" />
                </Button>
              </div>
            </div>
          )}
        </TabsContent>

        <TabsContent value="guide-approvals">
          <GuideApprovalsAudit />
        </TabsContent>
      </Tabs>
    </div>
  )
}
